import {LinkWrapper} from 'src/common/LinkWrapper'
import cn from 'classnames'
import {NavigationDocumentDataMenuLinksItem, SubmenuDocumentData} from 'prismicio-types'
import {ReactNode} from 'react'
import {isFilled} from '@prismicio/client'
import {ButtonV2} from 'src/common/ButtonV2'
import {MobileSubmenu} from './components/MobileSubmenu'
import {Icon} from 'src/common/Icon'

type NavElementProps = {
  index: number
  section: NavigationDocumentDataMenuLinksItem
  activeSubmenu: ReactNode
  isMobile: boolean
  isSubmenuOpen: boolean
  setIsMenuOpen: (value: boolean) => void
  activeIndex: number | null
  setActiveIndex: (value: number) => void
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  menuLinks: number
  pageUid: string
  isDarkBackground?: boolean
}

export const NavElement = ({
  index,
  section,
  menuLinks,
  pageUid,
  activeIndex,
  activeSubmenu,
  handleMouseEnter,
  handleMouseLeave,
  setActiveIndex,
  isDarkBackground,
  isSubmenuOpen,
  isMobile,
}: NavElementProps) => {
  const hasSubmenu = isFilled.contentRelationship<string, string, SubmenuDocumentData>(section.submenu)
  const isActive = activeIndex === index

  return (
    <>
      <div
        key={index}
        className={cn(
          'group flex h-full flex-col items-start text-[12px] font-semibold leading-[normal] tracking-[.72px] max-lg:border-b-[1px] max-lg:border-black/[10%] max-lg:text-[18px] max-lg:font-normal max-lg:leading-[23px] max-lg:text-black lg:items-center lg:py-0',
          {
            'lg:text-moss-white/[80%]': isActive && isDarkBackground,
            'lg:text-black/[70%]': isActive && !isDarkBackground,
            'lg:hover:text-moss-white/[80%]': isDarkBackground,
            'lg:hover:text-black/[70%]': !isDarkBackground,
          },
        )}
        {...(!hasSubmenu && {onMouseLeave: handleMouseLeave})}
      >
        <LinkWrapper
          className="w-full lg:h-full"
          link={section.link_url}
          mktgId={section.marketingid || ''}
          dataType={`order-${index}`}
          location="main-nav-bar"
          pageName={pageUid}
        >
          <div
            className={cn('flex h-full w-full items-center justify-between gap-2 max-lg:px-4 lg:gap-3 lg:p-0', {
              'max-lg:py-3 cursor-pointer': hasSubmenu,
              'max-lg:py-4': !hasSubmenu,
            })}
          >
            <span
              onMouseEnter={() => setActiveIndex(index)}
              className={cn('block w-full tracking-[.64px] lg:my-auto lg:w-fit lg:uppercase lg:tracking-[.8px]', {
                'pt-0': index === 0,
                'pb-0': index === menuLinks - 1,
              })}
            >
              {section.link_label}
            </span>
            {hasSubmenu && (
              <ButtonV2
                variant="icon-secondary"
                size="small"
                className="relative max-h-8 min-w-8 !rounded-[3px] lg:max-h-1.5 lg:min-w-1.5 lg:max-w-2.5 lg:bg-transparent lg:p-0 lg:hover:bg-transparent"
                trackingData={{
                  mktgId: section.link_label + '-mobile-button',
                  dataType: `order-${index}`,
                  location: 'main-nav-bar',
                  pageName: pageUid,
                }}
                border={false}
              >
                <Icon
                  name="chevron-v2"
                  className={cn('h-2.5 min-w-[10px] text-moss-grey-500 transition-transform duration-300', {
                    'rotate-0': isActive,
                    'rotate-180': !isActive,
                    'lg:text-moss-grey-500 lg:group-hover:text-black/[70%]': !isDarkBackground,
                    'lg:text-moss-white lg:group-hover:text-moss-white/[80%]': isDarkBackground,
                    'lg:text-black/[70%]': isActive && !isDarkBackground,
                    'lg:text-moss-white/[80%]': isActive && isDarkBackground,
                  })}
                />
              </ButtonV2>
            )}
          </div>
        </LinkWrapper>
      </div>
      <MobileSubmenu
        isOpen={isSubmenuOpen && isActive && isMobile}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
      >
        {activeSubmenu}
      </MobileSubmenu>
    </>
  )
}
